// src/hooks/useAuthPatched.js
// This module re-exports useAuth0 with E2E test mode support
// Import this instead of directly importing from @auth0/auth0-react

import { useAuth0 as useAuth0Original } from '@auth0/auth0-react';

// Mock user data for E2E testing
const mockUser = {
  sub: 'auth0|mock-user-id',
  email: 'e2e-test@example.com',
  email_verified: true,
  name: 'E2E Test User',
  nickname: 'e2e-test',
  picture: 'https://via.placeholder.com/150',
  updated_at: new Date().toISOString(),
};

// Mock auth context for E2E testing
const mockAuthContext = {
  isAuthenticated: true,
  isLoading: false,
  user: mockUser,
  loginWithRedirect: () => Promise.resolve(),
  loginWithPopup: () => Promise.resolve(),
  logout: () => {
    localStorage.removeItem('e2e_mock_auth_mode');
    sessionStorage.removeItem('mfaSessionToken');
    window.location.href = '/';
  },
  getAccessTokenSilently: () => Promise.resolve('mock-access-token-for-e2e-tests'),
  getAccessTokenWithPopup: () => Promise.resolve('mock-access-token-for-e2e-tests'),
  getIdTokenClaims: () => Promise.resolve({
    __raw: 'mock-id-token',
    ...mockUser,
    iss: 'https://localhost/',
    aud: 'local-client-id',
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 86400,
  }),
  error: undefined,
};

/**
 * Mock auth context for E2E tests that deliberately want to simulate
 * an unauthenticated user. The session-security suite needs a way to
 * verify that protected routes refuse to render their authenticated
 * shells; without this override, fresh `browser.newContext()` pages
 * still inherited authenticated state from whichever auth source the
 * build resolved to (real Auth0 SDK racing against the test, mock-auth
 * leaking via storageState, etc.), and the auth-guard locators in
 * `session-security.spec.ts` quietly degraded into no-ops.
 *
 * `loginWithRedirect` here is intentionally a no-op promise. The real
 * SPA SDK would navigate the browser to Auth0, which obviously cannot
 * succeed in a test environment with no Auth0 tenant configured — and
 * the existing `ProtectedRoute` already returns `null` synchronously
 * before that promise can settle, so the test sees an empty shell
 * without the authenticated content.
 */
const unauthenticatedMockContext = {
  isAuthenticated: false,
  isLoading: false,
  user: undefined,
  loginWithRedirect: () => Promise.resolve(),
  loginWithPopup: () => Promise.resolve(),
  logout: () => {},
  getAccessTokenSilently: () => Promise.reject(new Error('Unauthenticated (E2E override)')),
  getAccessTokenWithPopup: () => Promise.reject(new Error('Unauthenticated (E2E override)')),
  getIdTokenClaims: () => Promise.resolve(undefined),
  error: undefined,
};

// Check if E2E test mode is enabled (called on each hook invocation for reactivity)
const isE2ETestMode = () => {
  return (
    process.env.REACT_APP_E2E_TEST_MODE === 'true' ||
    (typeof window !== 'undefined' && localStorage.getItem('e2e_mock_auth_mode') === 'true')
  );
};

/**
 * Explicit "force unauthenticated" override for E2E tests.
 *
 * Honored regardless of `e2e_mock_auth_mode` so a single test can opt
 * out of the suite-wide mock-authenticated default by setting
 * `localStorage.e2e-auth-state = 'unauthenticated'` BEFORE navigating
 * to the route under test. The resulting state is strictly less
 * privileged than the real auth flow (no user, no token, no claims),
 * so even if a real user accidentally landed on this flag in
 * production the worst that happens is they look signed out — there
 * is no path to elevation.
 */
const isE2EUnauthenticatedOverride = () => {
  return (
    typeof window !== 'undefined' &&
    localStorage.getItem('e2e-auth-state') === 'unauthenticated'
  );
};

/**
 * Patched useAuth0 hook that returns mock data in E2E test mode.
 * This allows E2E tests to bypass Auth0 authentication entirely.
 */
export const useAuth0 = () => {
  const realAuth = useAuth0Original();

  // The explicit unauthenticated override wins over the mock-auth
  // default so the session-security suite can build a truly
  // unauthenticated context inside the same browser the rest of the
  // suite drives in mock-authenticated mode.
  if (isE2EUnauthenticatedOverride()) {
    return unauthenticatedMockContext;
  }

  // In E2E test mode, return mock auth data
  if (isE2ETestMode()) {
    return mockAuthContext;
  }

  return realAuth;
};

export default useAuth0;
